import { lazy } from 'react';
import { APP_EVENTS, WEB_EVENTS, GAMEPLAY_BLOCK_TYPES, NOTIFICATION_TYPE } from './events.js';

const AppBridge = lazy(
	() =>
		import(
			/* webpackChunkName: "app-bridge" */
			'./app-bridge.js'
		)
);

const iosBridgeEnabled = () => window.webkit?.messageHandlers?.ZHBridge?.postMessage;

const androidBridgeEnabled = () => window.CmnAndroid?.postMessage;

const isBridgeAvailable = () => !!(iosBridgeEnabled() || androidBridgeEnabled());

const isInternalInAppBrowser = () => !!window.__comeon?.internalInAppBrowser;

/**
 * Send a web event to Mobile Apps
 * using the platform specific injected object
 *
 * @param {string} name - Name of the WebEvent.
 * @param {Object[]} [args=[]] - Event params as object array.
 * @param {Function} [callback] - callback func to get a result from App.
 */
const sendAppBridgeEvent = (name, args = [], callback) => {
	const action = {
		id: 0,
		name,
		args,
		argsCount: args.length
	};
	try {
		const msg = JSON.stringify([action]);
		if (iosBridgeEnabled()) {
			window.webkit.messageHandlers.ZHBridge.postMessage(msg);
		} else if (androidBridgeEnabled()) {
			const rawResponse = window.CmnAndroid.postMessage(msg);
			if (rawResponse && callback) {
				const response = JSON.parse(rawResponse);
				if (response.success) {
					callback(response.args);
				} else {
					callback(null);
				}
			}
		}
	} catch (e) {
		// nvm
	}
};

const registerAppEventCallback = (appEvent, callback) => {
	if (isBridgeAvailable()) {
		if (!window.appEventCallbacks) {
			window.appEventCallbacks = {};
		}
		window.appEventCallbacks[appEvent] = callback;
	}
};

const unregisterAppEventCallback = appEvent => {
	if (isBridgeAvailable() && window.appEventCallbacks) {
		const { [appEvent]: _, ...appEventCallbacks } = window.appEventCallbacks;
		window.appEventCallbacks = appEventCallbacks;
	}
};

/**
 * Displays a native notification in mobile apps.
 *
 * This function triggers a native notification on mobile devices using the
 * app's WebView bridge. The notification type and message are passed to the
 * mobile app via a specified event.
 *
 * @param {string} type - The type of the notification, defined in {@link NOTIFICATION_TYPE}.
 * @param {string} message - The content of the notification to be displayed.
 */
const showNativeNotification = (type, message) => {
	sendAppBridgeEvent(WEB_EVENTS.SHOW_NOTIFICATION, [
		{
			notificationPayload: {
				type,
				message
			}
		}
	]);
};

export {
	AppBridge,
	APP_EVENTS,
	WEB_EVENTS,
	GAMEPLAY_BLOCK_TYPES,
	NOTIFICATION_TYPE,
	iosBridgeEnabled,
	androidBridgeEnabled,
	isBridgeAvailable,
	isInternalInAppBrowser,
	sendAppBridgeEvent,
	registerAppEventCallback,
	unregisterAppEventCallback,
	showNativeNotification
};
